import React from 'react';
import { RefreshCw } from 'lucide-react';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
import { useAdminInfrastructure } from '../../hooks/useAdminInfrastructure';
import CustomSelect from '../../components/shared/CustomSelect';
import {
  KeyStatistics,
  RecentBookings,
  BookingStatusBreakdown,
  QuickActions,
  RevenueBreakdown,
  PlatformMetrics,
  RecentVendors,
  RecentPayments,
  RevenueTrendChart,
  BookingTrendChart,
  TopPerformers,
  SystemHealth,
  ActivityFeed,
  UserGrowthChart
} from '../../components/admin/dashboard';

const AdminDashboard = () => {
  const { analytics, isLoading: isLoadingAnalytics, period, setPeriod, periodOptions } = useAdminAnalytics('month');
  const { 
    recentBookings, 
    recentVendors, 
    recentPayments, 
    performance,
    isLoading: isLoadingDashboard 
  } = useAdminDashboard(period);
  const { infrastructure } = useAdminInfrastructure();

  const isLoading = isLoadingAnalytics || isLoadingDashboard;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">Overview of platform statistics. Revenue and payouts exclude private bookings.</p>
          </div>
          <div className="mt-4 sm:mt-0 w-full sm:w-48">
            <CustomSelect
              value={period}
              onChange={(value) => setPeriod(value)}
              options={periodOptions}
              size="sm"
              className="w-full"
            />
          </div>
        </div>

        {/* Key Statistics - 3 per row */}
        <KeyStatistics analytics={analytics} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Recent Bookings */}
          <RecentBookings bookings={recentBookings} />

          {/* Sidebar */}
          <div className="space-y-6">
            <BookingStatusBreakdown bookingsAnalytics={analytics?.bookings_analytics} />
            <QuickActions />
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <RevenueTrendChart trends={analytics?.trends?.revenue || []} />
          <BookingTrendChart trends={analytics?.trends?.bookings || []} />
        </div>

        {/* Analytics Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <RevenueBreakdown 
            revenueAnalytics={analytics?.revenue_analytics} 
            overview={analytics?.overview} 
          />
          <PlatformMetrics bookingsAnalytics={analytics?.bookings_analytics} />
        </div>

        {/* Top Performers & System Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <TopPerformers topPerformers={analytics?.top_performers || []} />
          <SystemHealth performance={performance} infrastructure={infrastructure} />
        </div>

        {/* User Growth & Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 min-h-[400px]">
          <div>
            <UserGrowthChart userAnalytics={analytics?.user_analytics} />
          </div>
          <div>
            <ActivityFeed />
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecentVendors vendors={recentVendors} />
          <RecentPayments payments={recentPayments} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
